import { Button, PageHeader, cn } from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { fetchReviewQueue } from '../../api/ai.js';
import { fetchUnreadCount } from '../../api/notifications.js';

import { ReviewQueuePage } from './ai/review-queue.js';
import { NotificationsInboxPage } from './notifications/inbox.js';

/**
 * One inbox for everything that needs a person: notifications, tasks
 * live on their own page, and vendor emails the AI could not file.
 */
export function InboxPage() {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'ai' ? 'ai' : 'notifications';
  const unreadQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: fetchUnreadCount,
  });
  const aiQuery = useQuery({
    queryKey: ['ai-review-queue'],
    queryFn: fetchReviewQueue,
  });

  function setView(next: 'notifications' | 'ai') {
    const copy = new URLSearchParams(params);
    if (next === 'notifications') copy.delete('view');
    else copy.set('view', 'ai');
    setParams(copy, { replace: true });
  }

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Inbox"
        description="Notifications and vendor emails waiting on a person."
      />
      <div className="flex flex-wrap gap-2">
        <FilterChip
          active={view === 'notifications'}
          onClick={() => setView('notifications')}
          count={unreadQuery.data?.count}
          label="Notifications"
        />
        <FilterChip
          active={view === 'ai'}
          onClick={() => setView('ai')}
          count={aiQuery.data?.items.length}
          label="AI suggestions"
        />
      </div>
      {view === 'ai' ? <ReviewQueuePage embedded /> : <NotificationsInboxPage embedded />}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'default' : 'secondary'}
      onClick={onClick}
      className={cn(!active && 'bg-surface')}
    >
      {label}
      {count != null && count > 0 ? (
        <span className={cn('tabular-nums', active ? 'text-primary-fg/80' : 'text-fg-muted')}>
          {count}
        </span>
      ) : null}
    </Button>
  );
}
